import React, { useContext, ReactNode, useCallback } from "react";
import {
  AnalyticsSurface,
  AnalyticsProperties,
  ActionType,
  OnboardingState,
} from "./analyticsConstants";
import { trackEvent, getUserOnboardingState } from "utils/analytics";
import {
  useCurrentSchoolId,
  useCurrentUserNoExcept,
  useUserActivityState,
} from "helpers/useCurrentUser";
import { alongEnv } from "utils/env";

export { ActionType } from "./analyticsConstants";

export interface AnalyticsContextType extends AnalyticsProperties {
  surface: AnalyticsSurface;
}

const AnalyticsContext = React.createContext<AnalyticsContextType>({
  surface: null,
  extraMetadata: {},
});

export const useAnalyticsContext = () => useContext(AnalyticsContext);

export function useAnalytics(identifier: string) {
  const context = useContext(AnalyticsContext);
  const currentUser = useCurrentUserNoExcept();
  const schoolId = useCurrentSchoolId();
  const userActivityState = useUserActivityState();

  const wrappedTrackEvent = useCallback(
    (event: {
      eventName: string;
      eventType: ActionType;
      analyticsSurface?: AnalyticsSurface;
      extraProperties?: AnalyticsProperties;
    }) => {
      const { surface: contextSurface, ...contextProperties } = context;
      const mergedSurface = event.analyticsSurface || contextSurface;
      let userOnboardingState: OnboardingState | undefined = undefined;
      if (currentUser) {
        userOnboardingState = getUserOnboardingState(currentUser);
      }
      const mergedProperties = {
        ...contextProperties,
        ...event.extraProperties,
        onboardingState: userOnboardingState,
        schoolId,
        userActivityState,
        extraMetadata: {
          ...contextProperties.extraMetadata,
          ...event.extraProperties?.extraMetadata,
        },
      };

      if (!mergedSurface && alongEnv() === "development") {
        console.warn(
          "Please use AnalyticsProvider to provide analytics attributes for " +
            identifier,
        );
      }

      trackEvent({
        eventName: event.eventName,
        eventType: event.eventType,
        analyticsSurface: mergedSurface,
        extraProperties: mergedProperties,
      });
    },
    [context, identifier, currentUser, schoolId, userActivityState],
  );

  return { trackEvent: wrappedTrackEvent };
}

interface AnalyticsProviderProps extends AnalyticsContextType {
  children: ReactNode;
}

export function AnalyticsProvider(props: AnalyticsProviderProps) {
  const { children, ...restProps } = props;
  return (
    <AnalyticsContext.Provider value={restProps}>
      {children}
    </AnalyticsContext.Provider>
  );
}
